import React, { useEffect, useState } from 'react';
import apiHelper from '../../utils/apiHelpers';
import Loader from '../common/Loader';
import Alert from '../common/Alert';
import Button from '../common/Button';
import Input from '../common/Input';
import ConfirmDialog from '../common/ConfirmDialog';

/**
 * Admin-only runtime configuration. Today that is just the chatbot's system
 * prompt, which used to live in config.ts and needed a deploy to change.
 */
const Settings = () => {
  const [prompt, setPrompt] = useState('');
  // What the server last confirmed, so the unsaved-changes state is real rather
  // than "the field has been focused".
  const [savedPrompt, setSavedPrompt] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

  useEffect(() => {
    apiHelper
      .getSettings()
      .then(response => {
        setPrompt(response.data.chatbotSystemPrompt || '');
        setSavedPrompt(response.data.chatbotSystemPrompt || '');
        setDefaultPrompt(response.data.defaults?.chatbotSystemPrompt || '');
        setLoading(false);
      })
      .catch(() => {
        setErrorMessage('Error al cargar la configuración.');
        setLoading(false);
      });
  }, []);

  const handleSave = () => {
    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');
    apiHelper
      .updateSettings({ chatbotSystemPrompt: prompt })
      .then(response => {
        // Take the server's copy: it trims, so echoing our own text back would
        // leave the screen claiming unsaved changes right after a save.
        const stored = response.data.chatbotSystemPrompt;
        setPrompt(stored);
        setSavedPrompt(stored);
        setSuccessMessage('Configuración guardada.');
        setSaving(false);
      })
      .catch(error => {
        setErrorMessage(
          error.response?.data?.message || 'Error al guardar la configuración.',
        );
        setSaving(false);
      });
  };

  const hasChanges = prompt !== savedPrompt;

  return (
    <div className="p-4 flex flex-col gap-y-4">
      <h1 className="text-2xl text-white">
        <i className="fas fa-sliders-h mr-2"></i>
        Configuración
      </h1>
      <h4>
        Ajustes que se pueden cambiar sin volver a desplegar la aplicación. Solo
        los administradores pueden verlos y editarlos.
      </h4>

      {loading && <Loader />}

      {!loading && (
        <>
          <Alert>{errorMessage}</Alert>
          {successMessage && <p className="text-green-400">{successMessage}</p>}

          <div className="flex flex-col gap-y-2">
            <label htmlFor="chatbotSystemPrompt" className="text-white">
              Prompt del asistente de IA
            </label>
            <p className="text-gray-400">
              Las instrucciones que recibe el modelo antes de cada conversación:
              su alcance, su tono, y cuándo usar cada herramienta. El cambio
              aplica a los mensajes nuevos, sin reiniciar el servidor.
            </p>
            <Input
              type="textarea"
              name="chatbotSystemPrompt"
              className="font-mono h-96"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Instrucciones del asistente"
              disabled={saving}
            />
            <p className="text-gray-400">
              {prompt.length} caracteres
              {hasChanges && ' · hay cambios sin guardar'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              className="w-full sm:w-1/2"
              iconClass="fas fa-save"
              onClick={handleSave}
              disabled={saving || !hasChanges || !prompt.trim()}>
              Guardar
            </Button>
            <Button
              className="w-full sm:w-1/2"
              iconClass="fas fa-undo"
              onClick={() => setShowRestoreConfirm(true)}
              disabled={saving || !defaultPrompt || prompt === defaultPrompt}>
              Restaurar el original
            </Button>
          </div>
          {saving && <Loader />}
        </>
      )}

      <ConfirmDialog
        open={showRestoreConfirm}
        title="Restaurar el prompt original"
        message="Se va a reemplazar el texto del editor por el prompt original de la aplicación. Todavía vas a tener que apretar Guardar para aplicarlo."
        confirmLabel="Restaurar"
        confirmIconClass="fas fa-undo"
        onConfirm={() => {
          setPrompt(defaultPrompt);
          setShowRestoreConfirm(false);
        }}
        onClose={() => setShowRestoreConfirm(false)}
      />
    </div>
  );
};

export default Settings;
